import React from 'react';
import { useApp } from '../context/AppContext';
import { getMarca } from '../utils/marca';

// Componentes de marca (white-label). Los colores salen de las variables CSS
// (--steel/--sky-dark/--sky/--sky-light), que AppContext sobrescribe según la
// marca del cliente. El nombre/tagline/logo salen del contexto (o del store como
// fallback, para usarse fuera del árbol de React).

// Logo de barras ascendentes (identidad de Riesgo Unido = Cliente 1), coloreado
// por variables CSS. Ya NO es el default: una instancia lo usa solo si su
// `cont_config.marca_logo_url` trae el centinela 'bars'.
export function LogoBars({ size = 48 }) {
  const bars = [
    { h: 0.45, y: 0.55, fill: 'var(--steel)', op: 0.7 },
    { h: 0.65, y: 0.35, fill: 'var(--sky-dark)', op: 0.85 },
    { h: 0.85, y: 0.15, fill: 'var(--sky)', op: 1 },
    { h: 1, y: 0, fill: 'var(--sky-light)', op: 1 },
  ];
  const bw = size * 0.18, gap = size * 0.05;
  const tw = bars.length * bw + (bars.length - 1) * gap;
  return (
    <svg width={tw} height={size} viewBox={`0 0 ${tw} ${size}`} fill="none">
      {bars.map((b, i) => (
        <rect key={i} x={i * (bw + gap)} y={b.y * size} width={bw} height={b.h * size} rx={bw * 0.3} fill={b.fill} opacity={b.op} />
      ))}
    </svg>
  );
}

// Logo NEUTRO por defecto (Corredata): anillo "C" abierto + nodo ("data"),
// coloreado por variables CSS. Es lo que ve una instancia sin marca configurada
// — deliberadamente genérico, sin rastro de ningún cliente.
export function LogoNeutro({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <path d="M35.5 14.4 A15 15 0 1 0 35.5 33.6" stroke="var(--sky)" strokeWidth="5.5" strokeLinecap="round" fill="none" />
      <circle cx="36" cy="24" r="5.5" fill="var(--sky-light)" />
    </svg>
  );
}

// Logo de marca: el logo propio del cliente si lo subió (data URI / URL), las
// barras si trae el centinela 'bars' (RU), o el logo neutro de Corredata si no
// hay nada. `marca` opcional (para pantallas que ya lo tienen); si no, del store.
export function LogoMarca({ size = 48, marca }) {
  const m = marca || getMarca();
  if (m?.logo_url === 'bars') return <LogoBars size={size} />;
  if (m?.logo_url) {
    // La escala agranda el logo dentro de su espacio; el maxWidth NO se escala
    // para que un logo ancho nunca desborde el contenedor (queda "encajado").
    const escala = Math.max(0.5, Math.min(2, Number(m.logo_escala) || 1));
    return <img src={m.logo_url} alt={m.nombre || 'Logo'} style={{ height: size * escala, width: 'auto', maxWidth: size * 4, objectFit: 'contain' }} />;
  }
  return <LogoNeutro size={size} />;
}

// Nombre en dos tonos: todo menos la última palabra en blanco, la última (o
// `nombre_corto` si viene) resaltada en --sky. Ej. "RIESGO UNIDO".
export function NombreMarca({ marca, fontSize = 20 }) {
  const m = marca || getMarca();
  const nombre = (m?.nombre || 'Corredata').trim();
  const corto = (m?.nombre_corto || '').trim();
  let base = nombre, resaltado = '';
  if (corto && nombre.toLowerCase().endsWith(corto.toLowerCase())) {
    // `nombre_corto` es el SUFIJO de `nombre` que va resaltado (ej. "Riesgo
    // Unido" + "Unido" → "Riesgo " | "Unido"; "Corredata" + "data" → "Corre" | "data").
    base = nombre.slice(0, nombre.length - corto.length);
    resaltado = nombre.slice(nombre.length - corto.length);
  } else {
    // Sin palabra resaltada (o no es sufijo): se resalta la última palabra.
    const partes = nombre.split(/\s+/);
    resaltado = partes.pop();
    base = partes.length ? partes.join(' ') + ' ' : '';
  }
  return (
    <div style={{ fontSize, fontWeight: 900, textTransform: 'uppercase', lineHeight: 1 }}>
      {base && <span style={{ color: 'var(--text)' }}>{base}</span>}
      <span style={{ color: 'var(--sky)' }}>{resaltado}</span>
    </div>
  );
}

// Hook de conveniencia: la marca del contexto (o el store como fallback).
export function useMarca() {
  const ctx = useApp();
  return ctx?.marca || getMarca();
}
